// RoutesOption1.jsx - Day-Focused Column Layout
import React, { useState, useEffect, useCallback } from "react";
import "./RoutesOption1.css";
import { fetchTasksByDate, updateTask, filterTasksForActiveCustomers } from "../../Utils/api";
import { useAuth } from "../../Auth/AuthContext";

const RoutesOption1 = () => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [activeDate, setActiveDate] = useState(today.getDate());
  const [weekTasks, setWeekTasks] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTechName, setSelectedTechName] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("Not Started");
  const [viewMode, setViewMode] = useState("comfortable"); // "compact" or "comfortable"
  const [collapsedDays, setCollapsedDays] = useState({});
  const [activeView, setActiveView] = useState("week"); // "week" or "graphical"
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { companyId } = useAuth();

  function getStartOfWeek(date) {
    const day = date.getDay();
    // Monday = 1, Sunday = 0. If Sunday, go back 6 days, otherwise go back (day - 1) days
    const diff = day === 0 ? -6 : 1 - day;
    const result = new Date(date);
    result.setDate(date.getDate() + diff);
    return result;
  }

  const buildIsoDateString = (year, month, date) => {
    const mm = String(month + 1).padStart(2, "0");
    const dd = String(date).padStart(2, "0");
    const yyyy = String(year);
    return `${yyyy}-${mm}-${dd}`;
  };

  const fetchTasksForWeek = useCallback(async (year, month, day) => {
    if (!companyId) {
      console.log("No companyId available, skipping fetch");
      return;
    }

    setIsLoading(true);
    const startOfWeek = getStartOfWeek(new Date(year, month, day));
    const weekData = {};

    try {
      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        const isoDate = buildIsoDateString(date.getFullYear(), date.getMonth(), date.getDate());

        try {
          const response = await fetchTasksByDate(companyId, isoDate);

        // Check if response is valid and is an array with data
        if (response && Array.isArray(response) && response.length > 0) {
          // Filter tasks to only include active customers
          const activeTasks = filterTasksForActiveCustomers(response);

          const tasks = activeTasks
            .filter(item => item && item.taskDetails && item.customer && item.employee) // Filter out invalid items
            .map((item) => ({
              taskId: item.taskDetails.taskId,
              customerName: `${item.customer.custFirstName} ${item.customer.custLastName}`,
              status: item.taskDetails.taskStatus,
              assigneeName: `${item.employee.firstName} ${item.employee.lastName}`,
              employeeId: item.employee.employeeId,
              employeePhone: item.employee.contactNumber || '',
              companyId: item.taskDetails.companyId,
              customerId: item.customer.customerId,
              poolId: item.taskDetails.poolId,
              workOrderId: item.taskDetails.wrkOdrId,
              workOrderTaskDueDate: item.taskDetails.workOrderTaskDueDate,
              occurrenceDate: item.taskDetails.occurrenceDate,
              materialized: item.taskDetails.materialized,
            }));

          // Group by technician
          const technicianTasks = {};
          tasks.forEach(task => {
            if (!technicianTasks[task.employeeId]) {
              technicianTasks[task.employeeId] = {
                name: task.assigneeName,
                phone: task.employeePhone,
                total: 0,
                completed: 0,
                inProgress: 0,
                notStarted: 0,
                cancelled: 0,
                invalidated: 0,
                completedCustomers: [],
                inProgressCustomers: [],
                notStartedCustomers: [],
                cancelledCustomers: [],
                invalidatedCustomers: [],
                completedTasks: [],
                inProgressTasks: [],
                notStartedTasks: [],
                cancelledTasks: [],
                invalidatedTasks: []
              };
            }
            technicianTasks[task.employeeId].total++;
            if (task.status === "Completed") {
              technicianTasks[task.employeeId].completed++;
              technicianTasks[task.employeeId].completedCustomers.push(task.customerName);
              technicianTasks[task.employeeId].completedTasks.push(task);
            } else if (task.status === "In-Progress") {
              technicianTasks[task.employeeId].inProgress++;
              technicianTasks[task.employeeId].inProgressCustomers.push(task.customerName);
              technicianTasks[task.employeeId].inProgressTasks.push(task);
            } else if (task.status === "Cancelled") {
              technicianTasks[task.employeeId].cancelled++;
              technicianTasks[task.employeeId].cancelledCustomers.push(task.customerName);
              technicianTasks[task.employeeId].cancelledTasks.push(task);
            } else if (task.status === "Invalidated") {
              technicianTasks[task.employeeId].invalidated++;
              technicianTasks[task.employeeId].invalidatedCustomers.push(task.customerName);
              technicianTasks[task.employeeId].invalidatedTasks.push(task);
            } else {
              technicianTasks[task.employeeId].notStarted++;
              technicianTasks[task.employeeId].notStartedCustomers.push(task.customerName);
              technicianTasks[task.employeeId].notStartedTasks.push(task);
            }
          });

          weekData[isoDate] = {
            date: date,
            tasks: tasks,
            technicians: technicianTasks,
            completed: tasks.filter(t => t.status === "Completed").length,
            inProgress: tasks.filter(t => t.status === "In-Progress").length,
            notStarted: tasks.filter(t => t.status !== "Completed" && t.status !== "In-Progress" && t.status !== "Cancelled" && t.status !== "Invalidated").length,
            cancelled: tasks.filter(t => t.status === "Cancelled").length,
            invalidated: tasks.filter(t => t.status === "Invalidated").length,
          };
        } else {
          weekData[isoDate] = {
            date: date,
            tasks: [],
            technicians: {},
            completed: 0,
            inProgress: 0,
            notStarted: 0,
            cancelled: 0,
            invalidated: 0,
          };
        }
        } catch (error) {
          console.error("Failed to load tasks for", isoDate, error);
          weekData[isoDate] = {
            date: date,
            tasks: [],
            technicians: {},
            completed: 0,
            inProgress: 0,
            notStarted: 0,
            cancelled: 0,
            invalidated: 0,
          };
        }
      }
      setWeekTasks(weekData);
    } catch (error) {
      console.error("Error fetching week tasks:", error);
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchTasksForWeek(currentYear, currentMonth, activeDate);
  }, [currentMonth, currentYear, activeDate, fetchTasksForWeek]);

  const goToPreviousWeek = () => {
    const newDate = new Date(currentYear, currentMonth, activeDate - 7);
    // Batch all state updates together using React's automatic batching
    // This ensures only one re-render and one useEffect trigger
    const newMonth = newDate.getMonth();
    const newYear = newDate.getFullYear();
    const newDay = newDate.getDate();

    // Use startTransition to mark this as a non-urgent update for better performance
    React.startTransition(() => {
      setCurrentMonth(newMonth);
      setCurrentYear(newYear);
      setActiveDate(newDay);
    });
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentYear, currentMonth, activeDate + 7);
    // Batch all state updates together using React's automatic batching
    // This ensures only one re-render and one useEffect trigger
    const newMonth = newDate.getMonth();
    const newYear = newDate.getFullYear();
    const newDay = newDate.getDate();

    // Use startTransition to mark this as a non-urgent update for better performance
    React.startTransition(() => {
      setCurrentMonth(newMonth);
      setCurrentYear(newYear);
      setActiveDate(newDay);
    });
  };

  // Infinite navigation in both directions — no future limit
  const isNextWeekDisabled = false;

  const handleShowTasks = (tasks, date, techName, status) => {
    setSelectedTasks(tasks);
    setSelectedDate(date);
    setSelectedTechName(techName);
    setSelectedStatus(status);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTasks([]);
    setSelectedDate("");
    setSelectedTechName("");
    setSelectedStatus("Not Started");
  };

  const handleCancelJob = async (task) => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to cancel the job for ${task.customerName}?`
    );

    if (!confirmed) {
      return; // User cancelled the action
    }

    try {
      const updatedData = {
        // taskId may be null for a virtual (not-yet-materialized) occurrence; the server
        // materializes it from wrkOdrId + occurrenceDate before cancelling.
        taskId: task.taskId ?? null,
        poolId: task.poolId,
        wrkOdrId: task.workOrderId,
        empId: task.employeeId,
        workOrderTaskDueDate: task.workOrderTaskDueDate,
        occurrenceDate: task.occurrenceDate ?? null,
        taskStatus: "Cancelled"
      };

      await updateTask(updatedData);

      // Refresh the week data
      await fetchTasksForWeek(currentYear, currentMonth, activeDate);

      // Update the modal list — identify by a composite key so multiple virtual occurrences
      // (all with taskId == null) are not collapsed into one on a single cancel.
      const idOf = (t) => t.taskId ?? `${t.workOrderId}#${t.occurrenceDate}`;
      const cancelledId = idOf(task);
      const updatedTasks = selectedTasks.filter(t => idOf(t) !== cancelledId);
      setSelectedTasks(updatedTasks);

      // Show success message
      alert(`Job for ${task.customerName} has been cancelled successfully.`);

      // Close modal if no more tasks
      if (updatedTasks.length === 0) {
        handleCloseModal();
      }
    } catch (error) {
      console.error("Error cancelling job:", error);
      alert("Failed to cancel the job. Please try again.");
    }
  };

  const toggleDayCollapse = (dateKey) => {
    setCollapsedDays(prev => ({
      ...prev,
      [dateKey]: !prev[dateKey]
    }));
  };

  // Get unique employees from week data
  const getUniqueEmployees = () => {
    const employeesMap = {};

    // Check if weekTasks is valid and has data
    if (!weekTasks || typeof weekTasks !== 'object' || Object.keys(weekTasks).length === 0) {
      return [];
    }

    Object.values(weekTasks).forEach(dayData => {
      // Validate dayData and technicians
      if (dayData && dayData.technicians && typeof dayData.technicians === 'object') {
        Object.entries(dayData.technicians).forEach(([employeeId, techData]) => {
          if (employeeId && techData && techData.name && !employeesMap[employeeId]) {
            employeesMap[employeeId] = { name: techData.name, phone: techData.phone || '' };
          }
        });
      }
    });

    return Object.entries(employeesMap).map(([id, data]) => ({ id, name: data.name, phone: data.phone }));
  };

  // Get chart data for selected employee
  const getEmployeeChartData = () => {
    if (!selectedEmployee) return [];

    // Validate weekTasks
    if (!weekTasks || typeof weekTasks !== 'object' || Object.keys(weekTasks).length === 0) {
      return [];
    }

    const chartData = [];
    const sortedDays = Object.keys(weekTasks).sort();

    sortedDays.forEach(dateKey => {
      const dayData = weekTasks[dateKey];

      // Validate dayData
      if (!dayData || !dayData.date || !dayData.technicians) {
        return;
      }

      const techData = dayData.technicians[selectedEmployee];

      chartData.push({
        date: dateKey,
        dayName: ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"][dayData.date.getDay()],
        formattedDate: `${dayData.date.getMonth() + 1}/${dayData.date.getDate()}`,
        completed: techData?.completed || 0,
        inProgress: techData?.inProgress || 0,
        notStarted: techData?.notStarted || 0,
        cancelled: techData?.cancelled || 0,
        invalidated: techData?.invalidated || 0,
      });
    });

    return chartData;
  };

  const renderGraphicalView = () => {
    const employees = getUniqueEmployees();
    const chartData = getEmployeeChartData();
    const employeeName = employees.find(emp => emp.id === selectedEmployee)?.name || "";

    // Get date range for the week
    const sortedDays = Object.keys(weekTasks).sort();
    let dateRange = "";
    if (sortedDays.length > 0) {
      const firstDay = weekTasks[sortedDays[0]]?.date;
      const lastDay = weekTasks[sortedDays[sortedDays.length - 1]]?.date;
      if (firstDay && lastDay) {
        const formatDate = (date) => `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
        dateRange = `${formatDate(firstDay)} - ${formatDate(lastDay)}`;
      }
    }

    // Calculate totals for pie chart
    const totals = chartData.reduce(
      (acc, day) => ({
        completed: acc.completed + day.completed,
        inProgress: acc.inProgress + day.inProgress,
        notStarted: acc.notStarted + day.notStarted,
        cancelled: acc.cancelled + day.cancelled,
        invalidated: acc.invalidated + day.invalidated,
      }),
      { completed: 0, inProgress: 0, notStarted: 0, cancelled: 0, invalidated: 0 }
    );

    const totalTasks = totals.completed + totals.inProgress + totals.notStarted + totals.cancelled + totals.invalidated;

    // Calculate pie chart segments
    const pieData = [
      { label: 'Completed', value: totals.completed, color: '#4caf50', percentage: totalTasks > 0 ? (totals.completed / totalTasks * 100).toFixed(1) : 0 },
      { label: 'In Progress', value: totals.inProgress, color: '#ff9800', percentage: totalTasks > 0 ? (totals.inProgress / totalTasks * 100).toFixed(1) : 0 },
      { label: 'Not Started', value: totals.notStarted, color: '#f44336', percentage: totalTasks > 0 ? (totals.notStarted / totalTasks * 100).toFixed(1) : 0 },
      { label: 'Cancelled', value: totals.cancelled, color: '#9e9e9e', percentage: totalTasks > 0 ? (totals.cancelled / totalTasks * 100).toFixed(1) : 0 },
      { label: 'Invalidated', value: totals.invalidated, color: '#7b1fa2', percentage: totalTasks > 0 ? (totals.invalidated / totalTasks * 100).toFixed(1) : 0 },
    ].filter(item => item.value > 0);

    // Calculate pie chart path
    const createPieSlice = (startAngle, endAngle, radius, cx, cy) => {
      const startX = cx + radius * Math.cos(startAngle);
      const startY = cy + radius * Math.sin(startAngle);
      const endX = cx + radius * Math.cos(endAngle);
      const endY = cy + radius * Math.sin(endAngle);
      const largeArcFlag = endAngle - startAngle > Math.PI ? 1 : 0;

      return `M ${cx} ${cy} L ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY} Z`;
    };

    return (
      <div className="graphical-view">
        <div className="graphical-view-content">
          <div className="employee-selector-section">
            <label htmlFor="employee-select">Select Employee:</label>
            <select
              id="employee-select"
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="employee-dropdown"
            >
              <option value="">-- Choose an employee --</option>
              {employees.map(emp => {
                const duplicates = employees.filter(e => e.name === emp.name);
                const displayName = duplicates.length > 1 && emp.phone
                  ? `${emp.name} (${emp.phone})`
                  : emp.name;
                return (
                  <option key={emp.id} value={emp.id}>{displayName}</option>
                );
              })}
            </select>
            {employees.length === 0 && (
              <div className="no-employees-message">No employees found for this week</div>
            )}
          </div>

          {selectedEmployee && chartData.length > 0 && totalTasks > 0 ? (
            <div className="pie-chart-container">
              <h3 className="chart-title">
                {employeeName} - Weekly Summary {dateRange && `(${dateRange})`}
              </h3>
              <div className="pie-chart-wrapper">
                <svg width="250" height="250" viewBox="0 0 250 250">
                  {(() => {
                    let currentAngle = -Math.PI / 2; // Start at top
                    return pieData.map((slice, index) => {
                      const sliceAngle = (slice.value / totalTasks) * 2 * Math.PI;
                      const path = createPieSlice(currentAngle, currentAngle + sliceAngle, 100, 125, 125);
                      currentAngle += sliceAngle;

                      return (
                        <g key={index}>
                          <path
                            d={path}
                            fill={slice.color}
                            stroke="#fff"
                            strokeWidth="2"
                          />
                        </g>
                      );
                    });
                  })()}
                  {/* Center circle for donut effect */}
                  <circle cx="125" cy="125" r="50" fill="white" />
                  <text x="125" y="120" textAnchor="middle" fontSize="24" fontWeight="bold" fill="#333">
                    {totalTasks}
                  </text>
                  <text x="125" y="140" textAnchor="middle" fontSize="12" fill="#666">
                    Total Tasks
                  </text>
                </svg>
                <div className="pie-chart-legend">
                  {pieData.map((slice, index) => (
                    <div key={index} className="pie-legend-item">
                      <span
                        className="pie-legend-color"
                        style={{ backgroundColor: slice.color }}
                      ></span>
                      <span className="pie-legend-label">
                        {slice.label}: {slice.value} ({slice.percentage}%)
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="no-employee-selected">
              {selectedEmployee ? (totalTasks === 0 ? "No tasks found for this employee" : "No data available") : "Please select an employee to view the summary"}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const days = Object.keys(weekTasks).sort();
    const dayNames = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

    return (
      <div className={`week-columns ${viewMode}`}>
        {days.map((dateKey, index) => {
          const dayData = weekTasks[dateKey];
          const dayOfWeek = dayData.date.getDay();
          const formattedDate = `${dayData.date.getMonth() + 1}/${dayData.date.getDate()}`;
          const totalTasks = dayData.completed + dayData.inProgress + dayData.notStarted + dayData.cancelled;
          const techCount = Object.keys(dayData.technicians).length;
          const isCollapsed = collapsedDays[dateKey];
          const shouldShowCollapse = viewMode === "comfortable" && techCount > 3;

          return (
            <div key={dateKey} className={`day-column ${viewMode}`}>
              <div className="day-header">
                <div className="day-name">{dayNames[dayOfWeek]}</div>
                <div className="day-date">{formattedDate}</div>
                <div className="day-total">({totalTasks})</div>
              </div>

              {viewMode === "compact" && (
                <div className="status-counts">
                  <div className="status-item completed">
                    <span className="status-icon">✓</span>
                    <span className="status-count">{dayData.completed}</span>
                  </div>
                  <div className="status-item in-progress">
                    <span className="status-icon">⟳</span>
                    <span className="status-count">{dayData.inProgress}</span>
                  </div>
                  <div className="status-item not-started">
                    <span className="status-icon">○</span>
                    <span className="status-count">{dayData.notStarted}</span>
                  </div>
                  <div className="status-item cancelled">
                    <span className="status-icon">✗</span>
                    <span className="status-count">{dayData.cancelled}</span>
                  </div>
                </div>
              )}

              <div className="technicians-list">
                {shouldShowCollapse && (
                  <button
                    className="collapse-toggle"
                    onClick={() => toggleDayCollapse(dateKey)}
                  >
                    {isCollapsed ? `Show all ${techCount} technicians ▼` : `Collapse ▲`}
                  </button>
                )}

                {Object.values(dayData.technicians).map((tech, idx) => {
                  if (shouldShowCollapse && isCollapsed && idx >= 3) return null;

                  return (
                    <div key={idx} className="technician-item">
                      <span className="tech-name">{viewMode === "comfortable" ? tech.name : tech.name.split(' ')[0]}</span>
                      <div className="tech-counts">
                        {tech.completed > 0 && (
                          <span
                            className="tech-count completed clickable"
                            title={`Completed:\n${tech.completedCustomers.join('\n')}\n\nClick to view`}
                            onClick={() => handleShowTasks(tech.completedTasks, formattedDate, tech.name, "Completed")}
                          >
                            {viewMode === "comfortable" && <span className="tech-count-icon">✓</span>}
                            {tech.completed}
                          </span>
                        )}
                        {tech.inProgress > 0 && (
                          <span
                            className="tech-count in-progress clickable"
                            title={`In Progress:\n${tech.inProgressCustomers.join('\n')}\n\nClick to view`}
                            onClick={() => handleShowTasks(tech.inProgressTasks, formattedDate, tech.name, "In-Progress")}
                          >
                            {viewMode === "comfortable" && <span className="tech-count-icon">⟳</span>}
                            {tech.inProgress}
                          </span>
                        )}
                        {tech.notStarted > 0 && (
                          <span
                            className="tech-count not-started clickable"
                            title={`Not Started:\n${tech.notStartedCustomers.join('\n')}\n\nClick to manage jobs`}
                            onClick={() => handleShowTasks(tech.notStartedTasks, formattedDate, tech.name, "Not Started")}
                          >
                            {viewMode === "comfortable" && <span className="tech-count-icon">○</span>}
                            {tech.notStarted}
                          </span>
                        )}
                        {tech.cancelled > 0 && (
                          <span
                            className="tech-count cancelled clickable"
                            title={`Cancelled:\n${tech.cancelledCustomers.join('\n')}\n\nClick to view`}
                            onClick={() => handleShowTasks(tech.cancelledTasks, formattedDate, tech.name, "Cancelled")}
                          >
                            {viewMode === "comfortable" && <span className="tech-count-icon">✗</span>}
                            {tech.cancelled}
                          </span>
                        )}
                        {tech.invalidated > 0 && (
                          <span
                            className="tech-count invalidated clickable"
                            title={`Invalidated (cancelled check-in):\n${tech.invalidatedCustomers.join('\n')}\n\nClick to view`}
                            onClick={() => handleShowTasks(tech.invalidatedTasks, formattedDate, tech.name, "Invalidated")}
                          >
                            {viewMode === "comfortable" && <span className="tech-count-icon">⊘</span>}
                            {tech.invalidated}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {Object.keys(dayData.technicians).length === 0 && (
                  <div className="no-tasks">No tasks</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Calculate week totals with customer names
  const weekTotals = Object.values(weekTasks).reduce(
    (acc, day) => {
      // Collect customer names from all technicians
      Object.values(day.technicians).forEach(tech => {
        acc.completedCustomers.push(...tech.completedCustomers);
        acc.inProgressCustomers.push(...tech.inProgressCustomers);
        acc.notStartedCustomers.push(...tech.notStartedCustomers);
        acc.cancelledCustomers.push(...tech.cancelledCustomers);
      });

      return {
        completed: acc.completed + day.completed,
        inProgress: acc.inProgress + day.inProgress,
        notStarted: acc.notStarted + day.notStarted,
        cancelled: acc.cancelled + day.cancelled,
        completedCustomers: acc.completedCustomers,
        inProgressCustomers: acc.inProgressCustomers,
        notStartedCustomers: acc.notStartedCustomers,
        cancelledCustomers: acc.cancelledCustomers,
      };
    },
    {
      completed: 0,
      inProgress: 0,
      notStarted: 0,
      cancelled: 0,
      completedCustomers: [],
      inProgressCustomers: [],
      notStartedCustomers: [],
      cancelledCustomers: [],
    }
  );

  return (
    <div className="routes-option-1">
      <div className="page-header">
        <h2>Routes Summary</h2>
        <div className="header-controls">
          <div className="view-tabs">
            <button
              className={`view-tab-btn ${activeView === "week" ? "active" : ""}`}
              onClick={() => setActiveView("week")}
            >
              Week View
            </button>
            <button
              className={`view-tab-btn ${activeView === "graphical" ? "active" : ""}`}
              onClick={() => setActiveView("graphical")}
            >
              Graphical View
            </button>
          </div>
          <div className="legend">
            <div
              className="legend-item"
              title={weekTotals.completedCustomers.length > 0 ? `Completed:\n${weekTotals.completedCustomers.join('\n')}` : 'No completed tasks'}
            >
              <span className="legend-icon completed">✓</span>
              <span>Completed ({weekTotals.completed})</span>
            </div>
            <div
              className="legend-item"
              title={weekTotals.inProgressCustomers.length > 0 ? `In Progress:\n${weekTotals.inProgressCustomers.join('\n')}` : 'No in-progress tasks'}
            >
              <span className="legend-icon in-progress">⟳</span>
              <span>In Progress ({weekTotals.inProgress})</span>
            </div>
            <div
              className="legend-item"
              title={weekTotals.notStartedCustomers.length > 0 ? `Not Started:\n${weekTotals.notStartedCustomers.join('\n')}` : 'No not-started tasks'}
            >
              <span className="legend-icon not-started">○</span>
              <span>Not Started ({weekTotals.notStarted})</span>
            </div>
            <div
              className="legend-item"
              title={weekTotals.cancelledCustomers.length > 0 ? `Cancelled:\n${weekTotals.cancelledCustomers.join('\n')}` : 'No cancelled tasks'}
            >
              <span className="legend-icon cancelled">✗</span>
              <span>Cancelled ({weekTotals.cancelled})</span>
            </div>
          </div>
        </div>
      </div>

      {activeView === "week" && (
        <>
          <div className="week-navigation">
            <button onClick={goToPreviousWeek} disabled={isLoading}>
              ← Previous Week
            </button>
            <div className="week-navigation-center">
              <h3>
                {new Date(currentYear, currentMonth, activeDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                {isLoading && <span style={{ marginLeft: '10px', color: '#666', fontSize: '0.9em' }}>Loading...</span>}
              </h3>
              <div className="view-toggle">
                <button
                  className={`view-toggle-btn ${viewMode === "compact" ? "active" : ""}`}
                  onClick={() => setViewMode("compact")}
                  title="Compact view - fits more information"
                >
                  Compact
                </button>
                <button
                  className={`view-toggle-btn ${viewMode === "comfortable" ? "active" : ""}`}
                  onClick={() => setViewMode("comfortable")}
                  title="Comfortable view - easier to read"
                >
                  Comfortable
                </button>
              </div>
            </div>
            <button
              onClick={goToNextWeek}
              disabled={isNextWeekDisabled || isLoading}
              className={isNextWeekDisabled ? 'disabled' : ''}
              title="Next Week"
            >
              Next Week →
            </button>
          </div>

          {renderWeekView()}
        </>
      )}

      {activeView === "graphical" && (
        <>
          <div className="week-navigation">
            <button onClick={goToPreviousWeek} disabled={isLoading}>
              ← Previous Week
            </button>
            <div className="week-navigation-center">
              <h3>
                {new Date(currentYear, currentMonth, activeDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                {isLoading && <span style={{ marginLeft: '10px', color: '#666', fontSize: '0.9em' }}>Loading...</span>}
              </h3>
            </div>
            <button
              onClick={goToNextWeek}
              disabled={isNextWeekDisabled || isLoading}
              className={isNextWeekDisabled ? 'disabled' : ''}
              title="Next Week"
            >
              Next Week →
            </button>
          </div>

          {renderGraphicalView()}
        </>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedStatus} Jobs</h3>
              <button className="modal-close-btn" onClick={handleCloseModal}>×</button>
            </div>
            <div className="modal-subheader">
              <span className="modal-tech-name">{selectedTechName}</span>
              <span className="modal-date">{selectedDate}</span>
            </div>
            <div className="modal-body">
              {selectedTasks.length === 0 ? (
                <p className="no-jobs-message">All jobs have been cancelled</p>
              ) : (
                <ul className="jobs-list">
                  {selectedTasks.map((task, index) => (
                    <li key={task.taskId ?? `${task.workOrderId}#${task.occurrenceDate}` ?? index} className="job-item">
                      <span className="job-customer-name">{task.customerName}</span>
                      {selectedStatus === "Not Started" && (
                        <button
                          className="cancel-job-btn"
                          onClick={() => handleCancelJob(task)}
                        >
                          Cancel Job
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoutesOption1;
